import { useState, useEffect, useCallback } from "react";
import ThumbsUpBurst from "@/components/ThumbsUpBurst";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, XCircle, AlertCircle } from "lucide-react";
import { ReceiptCedi as Receipt } from "@/components/icons/CediIcons";
import { Button } from "@/components/ui/button";
import CustomerHeader from "@/components/CustomerHeader";

/**
 * Customer-side Paystack callback. Finalises invoice payments after the
 * customer returns from the Paystack hosted checkout. Marks the invoice
 * paid server-side (idempotent) and enqueues the Pro's MoMo payout.
 */
export default function CustomerPaymentCallback({ embedded = false, params = null, onDone = null }) {
  const { token } = useAuth();
  const [urlParams] = useSearchParams();
  const searchParams = params || urlParams;
  const routerNavigate = useNavigate();
  // In the Paystack slide-up, leaving the result closes the sheet first.
  const navigate = useCallback((to, opts) => { onDone?.(); routerNavigate(to, opts); }, [onDone, routerNavigate]);
  const [status, setStatus] = useState("verifying"); // verifying | success | failed | error
  const [message, setMessage] = useState("Verifying your payment...");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let tries = 0;
    const run = async () => {
      const ref = searchParams.get("reference") || searchParams.get("trxref");
      if (!ref) {
        setStatus("error");
        setMessage("No payment reference found");
        return;
      }
      if (!token) {
        setTimeout(() => {
          if (!token) {
            setStatus("error");
            setMessage("Please log in to verify your payment");
          }
        }, 1500);
        return;
      }
      try {
        if (searchParams.get("type") === "laundry_bill" || ref.startsWith("YLB-")) {
          const { data } = await axios.get(`${API}/laundry/paystack/verify/${ref}?token=${token}`);
          if (data.status === "paid") {
            setStatus("success");
            setMessage(`Payment to ${data.shop_title || "the laundry"} confirmed.`);
            toast.success("Laundry bill paid");
            window.dispatchEvent(new Event("yorm:booking-updated"));
            setTimeout(() => navigate("/customer/bookings", { replace: true }), 2500);
          } else if (data.status === "pending" && (tries += 1) < 15) {
            setMessage("Payment is still processing. We'll keep checking…");
            setTimeout(run, 3000);
          } else {
            setStatus("failed");
            setMessage(data.status === "pending" ? "We couldn't confirm this payment yet. If money left your account, it will show on your bill shortly." : "Payment was not successful");
          }
          return;
        }
        const res = await axios.get(`${API}/invoices/payment/verify/${ref}?token=${token}`);
        if (res.data?.status === "completed") {
          setStatus("success");
          setResult(res.data);
          setMessage(
            res.data?.artisan_name
              ? `Payment to ${res.data.artisan_name} confirmed.`
              : "Payment confirmed."
          );
          toast.success("Invoice paid");
          setTimeout(() => navigate("/customer/chats", { replace: true }), 3500);
        } else if (res.data?.status === "pending") {
          setMessage("Payment is still processing. We'll keep checking…");
          setTimeout(run, 3000);
        } else {
          setStatus("failed");
          setMessage(res.data?.message || "Payment was not successful");
        }
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "Failed to verify payment");
      }
    };
    run();
  }, [searchParams, token, navigate]);

  const card = (
        <div className={embedded ? "w-full bg-white p-6 text-center" : "max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center"} data-testid="customer-payment-callback">
          <div className="flex justify-center mb-6">
            {status === "verifying" && (
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
            )}
            {status === "success" && (
              <ThumbsUpBurst size={80} testId="payment-callback-thumbs-up" />
            )}
            {status === "failed" && (
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
            )}
            {status === "error" && (
              <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="h-12 w-12 text-yellow-600" />
              </div>
            )}
          </div>

          <h1 className={`text-2xl font-bold mb-2 ${
            status === "success" ? "text-green-600"
              : status === "failed" ? "text-red-600"
              : status === "error" ? "text-yellow-600"
              : "text-gray-800"
          }`}>
            {status === "verifying" && "Verifying Payment"}
            {status === "success" && "Payment Successful"}
            {status === "failed" && "Payment Failed"}
            {status === "error" && "Something went wrong"}
          </h1>

          <p className="text-gray-600 mb-6">{message}</p>

          {status === "success" && result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Receipt size={18} className="text-emerald-700" />
                <span className="font-semibold text-emerald-800">Invoice {result.invoice_number || ""}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount paid</span>
                <span className="font-semibold">GHS {Number(result.amount_ghs || 0).toFixed(2)}</span>
              </div>
            </div>
          )}

          {status === "success" && (
            <p className="text-xs text-gray-500 mb-4">{searchParams.get("type") === "laundry_bill" ? "Taking you to your bookings…" : "Redirecting to your messages…"}</p>
          )}

          {(status === "failed" || status === "error") && (
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/customer/chats")}
                className="w-full bg-[#4F988E] text-white hover:bg-[#3d7a72]"
                data-testid="payment-callback-back-btn"
              >
                Back to messages
              </Button>
              <Button
                variant="outline"
                onClick={() => (embedded ? onDone?.() : window.location.reload())}
                className="w-full"
                data-testid="payment-callback-retry-btn"
              >
                Try again
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t">
            <p className="text-xs text-gray-400">Powered by Yorm · Secured by Paystack</p>
          </div>
        </div>
  );
  if (embedded) return card;
  return (
    <div className="min-h-screen bg-gray-50">
      <CustomerHeader />
      <div className="flex items-center justify-center p-4 pt-20">{card}</div>
    </div>
  );
}
